import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Activity, Users, MousePointerClick, Sparkles, Globe, Monitor,
  Chrome, ExternalLink, RefreshCw, KeyRound, LogOut, AlertTriangle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { formatNumber } from "@/lib/format";
import BlogScheduler from "@/components/BlogScheduler";

const API = process.env.REACT_APP_BACKEND_URL;
const KEY_STORE = "cit-admin-key";
const RANGE_OPTIONS = [7, 30, 90, 365];

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

function readStoredKey() {
  try {
    return localStorage.getItem(KEY_STORE) || "";
  } catch {
    return "";
  }
}

async function fetchStats(key, days) {
  const res = await fetch(`${API}/api/admin/stats?days=${days}`, {
    headers: { "X-Admin-Key": key },
  });
  if (res.status === 401) throw new Error("unauthorized");
  if (!res.ok) throw new Error(`server error ${res.status}`);
  return res.json();
}

// -----------------------------------------------------------------------------
// Login gate
// -----------------------------------------------------------------------------

function KeyGate({ onSuccess, initialError }) {
  const [key, setKey] = useState("");
  const [error, setError] = useState(initialError || "");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!key.trim()) return;
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API}/api/admin/verify`, {
        headers: { "X-Admin-Key": key.trim() },
      });
      if (res.status === 401) {
        setError("Invalid admin key.");
      } else if (!res.ok) {
        setError("Server error. Try again.");
      } else {
        localStorage.setItem(KEY_STORE, key.trim());
        onSuccess(key.trim());
      }
    } catch (err) {
      setError("Network error. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4">
      <form
        onSubmit={submit}
        data-testid="admin-gate"
        className="card-flat w-full max-w-md p-8"
      >
        <div className="w-12 h-12 rounded-xl bg-primary/10 border border-primary/40 flex items-center justify-center mb-5">
          <KeyRound className="w-5 h-5 text-primary" />
        </div>
        <h1 className="text-2xl font-black tracking-tight">Admin access</h1>
        <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
          Enter your admin key to view the analytics dashboard. The key is stored locally in this browser so you don't have to type it again.
        </p>
        <div className="mt-6">
          <Input
            type="password"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            placeholder="Admin key"
            className="h-11 font-mono"
            data-testid="admin-key-input"
            autoFocus
          />
          {error && (
            <div className="mt-2 flex items-center gap-2 text-xs text-rose-500" data-testid="admin-gate-error">
              <AlertTriangle className="w-3.5 h-3.5" />
              {error}
            </div>
          )}
        </div>
        <Button
          type="submit"
          disabled={loading}
          className="btn-primary w-full h-11 mt-5 font-semibold"
          data-testid="admin-key-submit"
        >
          {loading ? "Verifying…" : "Enter dashboard"}
        </Button>
      </form>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Dashboard building blocks
// -----------------------------------------------------------------------------

function StatCard({ icon: Icon, label, value, hint, testId }) {
  return (
    <div className="card-flat p-5" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="overline">{label}</div>
        <Icon className="w-4 h-4 text-primary/80" />
      </div>
      <div className="mt-3 text-3xl font-black tracking-tight tabular-nums">{value}</div>
      {hint && <div className="mt-1 text-xs text-muted-foreground">{hint}</div>}
    </div>
  );
}

function BarList({ title, icon: Icon, items, labelKey = "label", valueKey = "count", empty, testId }) {
  const max = Math.max(1, ...items.map((i) => i[valueKey] || 0));
  return (
    <div className="card-flat p-5" data-testid={testId}>
      <div className="flex items-center gap-2 mb-4">
        <Icon className="w-4 h-4 text-primary/80" />
        <div className="overline">{title}</div>
      </div>
      {items.length === 0 ? (
        <div className="text-xs text-muted-foreground">{empty || "No data yet."}</div>
      ) : (
        <ul className="space-y-2.5">
          {items.map((it, idx) => {
            const val = it[valueKey] || 0;
            const pct = (val / max) * 100;
            return (
              <li key={`${it[labelKey] ?? "_"}-${idx}-${val}`} className="text-sm">
                <div className="flex items-center justify-between gap-3">
                  <span className="truncate font-medium" title={it[labelKey]}>
                    {it[labelKey] || "—"}
                  </span>
                  <span className="text-xs text-muted-foreground font-mono tabular-nums">
                    {formatNumber(val)}
                  </span>
                </div>
                <div className="mt-1.5 h-1.5 bg-secondary rounded-full overflow-hidden">
                  <div className="h-full bg-primary/80" style={{ width: `${pct}%` }} />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function DailyChart({ daily }) {
  const rows = daily || [];
  const max = Math.max(1, ...rows.map((r) => r.pageviews || 0));
  return (
    <div className="card-flat p-5" data-testid="admin-daily-chart">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-primary/80" />
          <div className="overline">Daily pageviews & sessions</div>
        </div>
        <div className="text-xs text-muted-foreground font-mono">{rows.length} days</div>
      </div>
      {rows.length === 0 ? (
        <div className="text-xs text-muted-foreground">No traffic yet in this range.</div>
      ) : (
        <div className="flex items-end gap-1 h-40">
          {rows.map((r) => {
            const h = ((r.pageviews || 0) / max) * 100;
            const hs = ((r.sessions || 0) / max) * 100;
            return (
              <div key={r.date} className="flex-1 flex flex-col items-center gap-0.5 group relative">
                <div className="w-full flex items-end gap-0.5 h-full">
                  <div
                    title={`${r.date} · ${r.pageviews} pageviews`}
                    className="w-full bg-primary/70 rounded-t-sm min-h-[2px]"
                    style={{ height: `${h}%` }}
                  />
                  <div
                    title={`${r.date} · ${r.sessions} sessions`}
                    className="w-full bg-primary/30 rounded-t-sm min-h-[2px]"
                    style={{ height: `${hs}%` }}
                  />
                </div>
                <div className="text-[9px] text-muted-foreground font-mono opacity-0 group-hover:opacity-100 absolute -bottom-4">
                  {r.date.slice(5)}
                </div>
              </div>
            );
          })}
        </div>
      )}
      <div className="mt-6 flex items-center gap-4 text-xs text-muted-foreground">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm bg-primary/70" /> Pageviews
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm bg-primary/30" /> Sessions
        </div>
      </div>
    </div>
  );
}

function RecentEventsTable({ events }) {
  return (
    <div className="card-flat p-5 overflow-hidden" data-testid="admin-recent-events">
      <div className="flex items-center gap-2 mb-4">
        <Activity className="w-4 h-4 text-primary/80" />
        <div className="overline">Live events (last 25)</div>
      </div>
      {events.length === 0 ? (
        <div className="text-xs text-muted-foreground">No events yet.</div>
      ) : (
        <div className="overflow-x-auto -mx-5 px-5">
          <table className="w-full text-xs font-mono">
            <thead className="text-muted-foreground/70">
              <tr className="text-left">
                <th className="py-2 pr-4">Time</th>
                <th className="py-2 pr-4">Type</th>
                <th className="py-2 pr-4">Path</th>
                <th className="py-2 pr-4">Referrer</th>
                <th className="py-2 pr-4">Country</th>
                <th className="py-2 pr-4">Device</th>
                <th className="py-2 pr-4">Browser</th>
              </tr>
            </thead>
            <tbody>
              {events.map((e) => (
                <tr key={`${e.created_at}-${e.path}-${e.type}`} className="border-t border-border/50">
                  <td className="py-2 pr-4 whitespace-nowrap">{e.created_at}</td>
                  <td className="py-2 pr-4">
                    <span className={`px-1.5 py-0.5 rounded ${e.type === "channel_analyzed" ? "bg-primary/10 text-primary" : "bg-secondary text-muted-foreground"}`}>
                      {e.type}
                    </span>
                  </td>
                  <td className="py-2 pr-4 max-w-[220px] truncate" title={e.path}>{e.path}</td>
                  <td className="py-2 pr-4 max-w-[160px] truncate" title={e.referrer}>{e.referrer || "—"}</td>
                  <td className="py-2 pr-4">{e.country || "—"}</td>
                  <td className="py-2 pr-4">{e.device}</td>
                  <td className="py-2 pr-4">{e.browser}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Main
// -----------------------------------------------------------------------------

export default function AdminDashboard() {
  const [params] = useSearchParams();
  const [key, setKey] = useState(() => params.get("key") || readStoredKey());
  const [days, setDays] = useState(30);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!key) return;
    setLoading(true);
    setError("");
    try {
      const data = await fetchStats(key, days);
      setStats(data);
    } catch (err) {
      if (err.message === "unauthorized") {
        setError("Your admin key is no longer valid.");
        setKey("");
        localStorage.removeItem(KEY_STORE);
      } else {
        setError("Failed to load stats.");
      }
    } finally {
      setLoading(false);
    }
  }, [key, days]);

  useEffect(() => {
    load();
  }, [load]);

  // Persist key coming from URL query on first successful load
  useEffect(() => {
    if (key && !readStoredKey()) {
      localStorage.setItem(KEY_STORE, key);
    }
  }, [key]);

  const logout = () => {
    localStorage.removeItem(KEY_STORE);
    setKey("");
    setStats(null);
  };

  const summaryCards = useMemo(() => {
    if (!stats) return [];
    return [
      { icon: Users, label: "Unique sessions", value: formatNumber(stats.unique_sessions), hint: `Last ${stats.period_days} days`, testId: "stat-sessions" },
      { icon: MousePointerClick, label: "Pageviews", value: formatNumber(stats.pageviews), hint: "Bots excluded", testId: "stat-pageviews" },
      { icon: Sparkles, label: "Channel analyses", value: formatNumber(stats.channel_analyses), hint: "AI diagnoses run", testId: "stat-analyses" },
      { icon: Activity, label: "Total events", value: formatNumber(stats.total_events), hint: "All tracked interactions", testId: "stat-events" },
    ];
  }, [stats]);

  if (!key) {
    return <KeyGate onSuccess={setKey} initialError={error} />;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-24" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="overline">Private dashboard</div>
          <h1 className="mt-1 text-3xl sm:text-4xl font-black tracking-tight">Analytics</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            First-party visitor and event data. Bots and admin visits are excluded.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-full border border-border/70 bg-secondary/40 p-1">
            {RANGE_OPTIONS.map((d) => (
              <button
                key={d}
                onClick={() => setDays(d)}
                data-testid={`range-${d}`}
                className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  days === d ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {d}d
              </button>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="admin-refresh">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button variant="ghost" size="sm" onClick={logout} data-testid="admin-logout">
            <LogOut className="w-3.5 h-3.5 mr-1.5" />
            Logout
          </Button>
        </div>
      </div>

      {error && (
        <div className="mt-4 card-flat p-4 border-rose-500/30 bg-rose-500/5 text-sm text-rose-500 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" /> {error}
        </div>
      )}

      {stats && (
        <>
          <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
            {summaryCards.map((c) => <StatCard key={c.label} {...c} />)}
          </div>

          <div className="mt-6">
            <DailyChart daily={stats.daily} />
          </div>

          <div className="mt-6 grid md:grid-cols-2 gap-4">
            <BarList
              title="Page categories (macro view)"
              icon={Monitor}
              items={stats.page_categories || []}
              testId="page-categories"
              empty="No pageviews yet in this range."
            />
            <BarList title="Top pages" icon={ExternalLink} items={stats.top_pages} testId="top-pages" />
            <BarList
              title="Most searched channels"
              icon={Sparkles}
              items={stats.top_searches || []}
              empty="Channel searches appear once visitors use the Analyze form."
              testId="top-searches"
            />
            <BarList
              title="Top analyzed channels"
              icon={Sparkles}
              items={(stats.top_channels || []).map((c) => ({ label: c.title || c.channel_id, count: c.count }))}
              testId="top-channels"
            />
            <BarList title="Traffic sources (referrers)" icon={ExternalLink} items={stats.top_referrers} testId="top-referrers" />
            <BarList title="Top countries" icon={Globe} items={stats.top_countries} empty="Geo data appears once the CDN forwards country headers." testId="top-countries" />
            <BarList title="Devices" icon={Monitor} items={stats.devices} testId="devices" />
            <BarList title="Browsers" icon={Chrome} items={stats.browsers} testId="browsers" />
          </div>

          <div className="mt-6">
            <RecentEventsTable events={stats.recent_events || []} />
          </div>

          <div className="mt-8 text-[11px] text-muted-foreground font-mono">
            Generated at {stats.generated_at}
          </div>

          <BlogScheduler />
        </>
      )}

      {loading && !stats && (
        <div className="mt-16 text-center text-muted-foreground text-sm">Loading analytics…</div>
      )}
    </div>
  );
}
